/**
 * Projects Filter & Sort
 * Client-side filtering and sorting for the Projects page.
 * Reads product metafield data from embedded JSON, then
 * shows/hides and reorders product cards accordingly.
 */
(function () {
  'use strict';

  /* ── Bootstrap ─────────────────────────────────────── */

  const metaEl = document.getElementById('ProjectsMetadata');
  if (!metaEl) return;

  let meta;
  try {
    meta = JSON.parse(metaEl.textContent);
  } catch (e) {
    console.error('Projects: failed to parse metadata', e);
    return;
  }

  const products = meta.products || [];
  const FIELD_ORDER = meta.field_order || [];

  /* Pre-compute derived fields */
  products.forEach((p) => {
    p._startMs = p.start_date ? new Date(p.start_date).getTime() : 0;
    p._endMs = p.end_date ? new Date(p.end_date).getTime() : 0;
    p._duration = p._endMs && p._startMs ? p._endMs - p._startMs : 0;
    /* Primary field index for field-based sorting */
    const primary = (p.fields && p.fields[0]) || '';
    p._fieldIdx = FIELD_ORDER.indexOf(primary);
    if (p._fieldIdx === -1) p._fieldIdx = FIELD_ORDER.length;
  });

  /* ── DOM references ────────────────────────────────── */

  const grid = document.querySelector('#ProjectGridContainer motion-list');
  if (!grid) return;

  const noResults = document.querySelector('.projects-no-results');
  const countEls = document.querySelectorAll('.projects-visible-count');
  const resultsCountEls = document.querySelectorAll('.projects-results-count');
  const facetCountEls = document.querySelectorAll('.projects-facet-count');
  const activeContainer = document.getElementById('ProjectsActiveFilters');
  const clearAllBtns = document.querySelectorAll('.projects-clear-all-btn');

  /* Build a map: productId → wrapper element */
  const cardMap = new Map();
  grid.querySelectorAll('.projects-product-card').forEach((el) => {
    cardMap.set(String(el.dataset.productId), el);
  });

  /* ── State ─────────────────────────────────────────── */

  let activeFields = new Set();
  let activeSkills = new Set();
  let activeSoftwareHardware = new Set();
  let activeVendor = '';
  let currentSort = 'start-date-desc';

  /* ── Filter checkboxes & sort selects ──────────────── */

  const allFilterCheckboxes = document.querySelectorAll('.projects-filter-checkbox');
  const allSortSelects = document.querySelectorAll('.projects-sort-select');
  const allSortRadios = document.querySelectorAll('.projects-sort-radio');

  /* ── Helpers ───────────────────────────────────────── */

  function passesFilter(product) {
    if (activeVendor) {
      if (product.vendor_handle !== activeVendor) return false;
    }
    if (activeFields.size > 0) {
      const hasField = product.fields.some((f) => activeFields.has(f));
      if (!hasField) return false;
    }
    if (activeSkills.size > 0) {
      const hasSkill = product.skills.some((s) => activeSkills.has(s));
      if (!hasSkill) return false;
    }
    if (activeSoftwareHardware.size > 0) {
      const hasSH = product.software_hardware.some((s) => activeSoftwareHardware.has(s));
      if (!hasSH) return false;
    }
    return true;
  }

  function getSortComparator() {
    switch (currentSort) {
      case 'start-date-desc':
        return (a, b) => b._startMs - a._startMs;
      case 'start-date-asc':
        return (a, b) => a._startMs - b._startMs;
      case 'end-date-desc':
        return (a, b) => b._endMs - a._endMs;
      case 'end-date-asc':
        return (a, b) => a._endMs - b._endMs;
      case 'duration-desc':
        return (a, b) => b._duration - a._duration;
      case 'duration-asc':
        return (a, b) => a._duration - b._duration;
      case 'field-asc':
        return (a, b) => a._fieldIdx - b._fieldIdx || a._startMs - b._startMs;
      case 'field-desc':
        return (a, b) => b._fieldIdx - a._fieldIdx || a._startMs - b._startMs;
      default:
        return (a, b) => b._startMs - a._startMs;
    }
  }

  /* ── Count helpers ─────────────────────────────────── */

  function computeFilterCounts() {
    /*
     * For each filter group, compute counts considering the OTHER
     * active filters but NOT the selections within the same group.
     * This lets users see how many results each option would produce.
     */

    /* Field counts – apply only skill + software_hardware filters */
    const fieldCounts = {};
    products.forEach((p) => {
      let pass = true;
      if (activeSkills.size > 0) {
        pass = p.skills.some((s) => activeSkills.has(s));
      }
      if (pass && activeSoftwareHardware.size > 0) {
        pass = p.software_hardware.some((s) => activeSoftwareHardware.has(s));
      }
      if (!pass) return;
      p.fields.forEach((f) => {
        fieldCounts[f] = (fieldCounts[f] || 0) + 1;
      });
    });

    /* Skill counts – apply only field + software_hardware filters */
    const skillCounts = {};
    products.forEach((p) => {
      let pass = true;
      if (activeFields.size > 0) {
        pass = p.fields.some((f) => activeFields.has(f));
      }
      if (pass && activeSoftwareHardware.size > 0) {
        pass = p.software_hardware.some((s) => activeSoftwareHardware.has(s));
      }
      if (!pass) return;
      p.skills.forEach((s) => {
        skillCounts[s] = (skillCounts[s] || 0) + 1;
      });
    });

    /* Software // Hardware counts – apply only field + skill filters */
    const shCounts = {};
    products.forEach((p) => {
      let pass = true;
      if (activeFields.size > 0) {
        pass = p.fields.some((f) => activeFields.has(f));
      }
      if (pass && activeSkills.size > 0) {
        pass = p.skills.some((s) => activeSkills.has(s));
      }
      if (!pass) return;
      p.software_hardware.forEach((s) => {
        shCounts[s] = (shCounts[s] || 0) + 1;
      });
    });

    document.querySelectorAll('[data-field-count]').forEach((el) => {
      el.textContent = '(' + (fieldCounts[el.dataset.fieldCount] || 0) + ')';
    });

    document.querySelectorAll('[data-skill-count]').forEach((el) => {
      el.textContent = '(' + (skillCounts[el.dataset.skillCount] || 0) + ')';
    });

    document.querySelectorAll('[data-software-hardware-count]').forEach((el) => {
      el.textContent = '(' + (shCounts[el.dataset.softwareHardwareCount] || 0) + ')';
    });
  }

  /* ── Render ────────────────────────────────────────── */

  function render() {
    /* 1. Determine visible products & sort them */
    const visible = products.filter(passesFilter);
    visible.sort(getSortComparator());

    const visibleIds = new Set(visible.map((p) => String(p.id)));
    const totalFilterCount = activeFields.size + activeSkills.size + activeSoftwareHardware.size + (activeVendor ? 1 : 0);

    /* 2. Show/hide cards */
    cardMap.forEach((el, id) => {
      el.style.display = visibleIds.has(id) ? '' : 'none';
    });

    /* 3. Visually reorder via CSS order (avoids moving DOM nodes,
          which would trigger disconnectedCallback and destroy
          Flickity carousels inside secondary-media elements) */
    visible.forEach((p, i) => {
      const el = cardMap.get(String(p.id));
      if (el) el.style.order = i;
    });

    /* 4. No-results state */
    if (noResults) {
      noResults.style.display = visible.length === 0 && products.length > 0 ? '' : 'none';
    }

    /* 5. Update counts */
    countEls.forEach((el) => (el.textContent = visible.length));
    resultsCountEls.forEach((el) => (el.textContent = visible.length));
    facetCountEls.forEach((el) => {
      el.textContent = totalFilterCount;
      el.hidden = totalFilterCount === 0;
    });

    /* 6. Recompute filter counts */
    computeFilterCounts();

    /* 7. Active filter pills */
    renderActivePills(totalFilterCount);

    /* 8. Recheck summary clamp after layout changes */
    requestAnimationFrame(function () {
      requestAnimationFrame(updateSummaryClamps);
    });
  }

  /* ── Active filter pills ───────────────────────────── */

  function renderActivePills(totalFilterCount) {
    if (!activeContainer) return;

    let html = '';

    if (activeVendor) {
      /* Find display name from products data */
      var vendorDisplay = activeVendor;
      for (var i = 0; i < products.length; i++) {
        if (products[i].vendor_handle === activeVendor) {
          vendorDisplay = products[i].vendor;
          break;
        }
      }
      html += buildPill(vendorDisplay, 'vendor');
    }

    activeFields.forEach((val) => {
      html += buildPill(val, 'field');
    });
    activeSkills.forEach((val) => {
      html += buildPill(val, 'skill');
    });
    activeSoftwareHardware.forEach((val) => {
      html += buildPill(val, 'software_hardware');
    });

    if (totalFilterCount > 1) {
      html += '<button type="button" class="facet-clear-all link text-xs lg:text-sm projects-clear-all-btn">Clear all</button>';
    }

    activeContainer.innerHTML = html;

    /* Bind pill remove buttons */
    activeContainer.querySelectorAll('[data-remove-filter]').forEach((btn) => {
      btn.addEventListener('click', function () {
        const type = this.dataset.removeType;
        const value = this.dataset.removeValue;
        if (type === 'field') activeFields.delete(value);
        if (type === 'skill') activeSkills.delete(value);
        if (type === 'software_hardware') activeSoftwareHardware.delete(value);
        if (type === 'vendor') {
          activeVendor = '';
          clearVendorParam();
        }
        syncCheckboxes();
        render();
      });
    });

    activeContainer.querySelectorAll('.projects-clear-all-btn').forEach((btn) => {
      btn.addEventListener('click', clearAll);
    });
  }

  function buildPill(value, type) {
    return '<div class="removable-facet flex items-center justify-between gap-3 lg:gap-4 text-xs lg:text-sm leading-none">' +
      '<div class="facet-title flex items-center gap-2">' + escapeHtml(value) + '</div>' +
      '<button type="button" class="block relative" data-remove-filter data-remove-type="' + type + '" data-remove-value="' + escapeHtml(value) + '" aria-label="Remove ' + escapeHtml(value) + '">' +
      '<span class="btn-text"><svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg></span>' +
      '</button></div>';
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /* ── Sync all checkbox instances ────────────────────── */

  function syncCheckboxes() {
    allFilterCheckboxes.forEach((cb) => {
      const type = cb.dataset.filterType;
      const value = cb.value;
      if (type === 'field') cb.checked = activeFields.has(value);
      if (type === 'skill') cb.checked = activeSkills.has(value);
      if (type === 'software_hardware') cb.checked = activeSoftwareHardware.has(value);
    });
  }

  function syncSortControls() {
    allSortSelects.forEach((sel) => (sel.value = currentSort));
    allSortRadios.forEach((radio) => (radio.checked = radio.value === currentSort));

    /* Update the desktop facet-sort selection label */
    const selectionEl = document.querySelector('#ProjectsSortByContainer .sort-selection');
    if (selectionEl) {
      const checkedRadio = document.querySelector('.projects-sort-radio:checked');
      if (checkedRadio) {
        const label = checkedRadio.nextElementSibling;
        if (label) selectionEl.textContent = label.textContent;
      }
    }
  }

  /* ── Clear all ─────────────────────────────────────── */

  function clearAll() {
    activeFields.clear();
    activeSkills.clear();
    activeSoftwareHardware.clear();
    if (activeVendor) {
      activeVendor = '';
      clearVendorParam();
    }
    syncCheckboxes();
    render();
  }

  /* ── URL vendor param helpers ────────────────────────── */

  function clearVendorParam() {
    var url = new URL(window.location);
    url.searchParams.delete('work-type');
    window.history.replaceState({}, '', url);
  }

  /* ── Event listeners ───────────────────────────────── */

  /* Filter checkboxes */
  allFilterCheckboxes.forEach((cb) => {
    cb.addEventListener('change', function () {
      const type = this.dataset.filterType;
      const value = this.value;
      const checked = this.checked;

      if (type === 'field') {
        checked ? activeFields.add(value) : activeFields.delete(value);
      } else if (type === 'skill') {
        checked ? activeSkills.add(value) : activeSkills.delete(value);
      } else if (type === 'software_hardware') {
        checked ? activeSoftwareHardware.add(value) : activeSoftwareHardware.delete(value);
      }

      /* Sync all duplicate checkboxes (drawer, sidebar, sticky) */
      allFilterCheckboxes.forEach((other) => {
        if (other !== this && other.dataset.filterType === type && other.value === value) {
          other.checked = checked;
        }
      });

      render();
    });
  });

  /* Sort selects (drawer / sidebar / mobile) */
  allSortSelects.forEach((sel) => {
    sel.addEventListener('change', function () {
      currentSort = this.value;
      syncSortControls();
      render();
    });
  });

  /* Sort radios (desktop topbar facet-sort) */
  allSortRadios.forEach((radio) => {
    radio.addEventListener('change', function () {
      currentSort = this.value;
      syncSortControls();
      render();
    });
  });

  /* Clear all buttons (outside active pills) */
  clearAllBtns.forEach((btn) => {
    btn.addEventListener('click', clearAll);
  });

  /* Sidebar toggle button */
  document.querySelectorAll('[is="projects-filters-toggle"]').forEach((btn) => {
    btn.addEventListener('click', function () {
      const target = document.getElementById(this.getAttribute('aria-controls'));
      if (!target) return;
      const expanded = this.getAttribute('aria-expanded') === 'true';
      this.setAttribute('aria-expanded', String(!expanded));
      target.style.display = expanded ? 'none' : '';
      /* After sidebar toggle changes card widths, reclamp summaries */
      requestAnimationFrame(function () {
        requestAnimationFrame(updateSummaryClamps);
      });
    });
  });

  /* ── Initial render ────────────────────────────────── */

  /* Read ?work-type= query param and pre-filter if present */
  (function () {
    var params = new URLSearchParams(window.location.search);
    var vendorParam = params.get('work-type');
    if (vendorParam) {
      activeVendor = vendorParam;
    }
  })();

  /* Compute initial counts */
  computeFilterCounts();

  /* Apply default sort (start-date-desc) on page load */
  render();

  /* ── Summary line-clamp calculation ──────────────────── */

  function updateSummaryClamps() {
    document.querySelectorAll('.proj-desc-summary').forEach(function (outer) {
      /* Skip hidden cards */
      if (outer.offsetParent === null) return;

      var inner = outer.querySelector('.proj-desc-summary__text');
      if (!inner) return;

      var style = getComputedStyle(outer);
      var lineHeight = parseFloat(style.lineHeight);
      if (!lineHeight || isNaN(lineHeight)) {
        /* fallback: font-size * 1.6 (matches the CSS line-height) */
        lineHeight = parseFloat(style.fontSize) * 1.6;
      }

      /* Available height = outer element's content box (minus padding) */
      var paddingTop = parseFloat(style.paddingTop) || 0;
      var paddingBottom = parseFloat(style.paddingBottom) || 0;
      var availableHeight = outer.clientHeight - paddingTop - paddingBottom;

      var lines = Math.floor(availableHeight / lineHeight);
      if (lines < 1) lines = 1;

      inner.style.setProperty('--summary-lines', String(lines));
    });
  }

  /* Use ResizeObserver for reliable reflow detection */
  if (typeof ResizeObserver !== 'undefined') {
    var ro = new ResizeObserver(function () {
      updateSummaryClamps();
    });
    document.querySelectorAll('.proj-desc-summary').forEach(function (el) {
      ro.observe(el);
    });
  }

  /* Also recalculate after fonts finish loading (text reflow) */
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () {
      updateSummaryClamps();
    });
  }

  /* Fallback: check on resize and after a short delay for late layout */
  window.addEventListener('resize', updateSummaryClamps);
  setTimeout(updateSummaryClamps, 300);
})();
